import RestClientService from "@/core/services/restClient.service";
import { ICoreResponse } from "@/core/interfaces/coreResponse.interface";
import notificationService from "@/core/services/notification.service";
import { NOTIFICATION_TYPES } from "@/core/enums/NOTIFICATION_TYPES";
import { NOTIFICATION_MESSAGES } from "@/core/constants/notificationMessages";
import {
  IPayment,
  IPayments,
} from "@/core/interfaces/state/payments.interface";
import { PaymentsResponseModel } from "@/modules/Payments/models/payments.response";
import { PaymentsMapper } from "@/modules/Payments/responseMappers/payments.mapper";
import { payGuideUrl, PAYMENTS_API_PATH } from "@/modules/Payments/constants";
import { FetchPaymentsParamsRequestModel } from "@/modules/Payments/models/fetchPaymentsParams.request";
import { CreatePaymentBodyRequest } from "@/modules/Payments/models/createPaymentBody.request";
import { PaymentDetailResponseModel } from "@/modules/Payments/models/paymentDetail.response";
import { UpdateBankTransferRequestModel } from "@/modules/Payments/models/updateBankTransfer.request";
import { formatString } from "@/core/utils/helpers";
import { NOTIFICATION_STYLES } from "@/core/enums/NOTIFICATION_STYLES";
import { PAYMENT_METHODS_TYPES } from "@/core/enums/PAYMENT_METHODS_TYPES";
import {
  ContentUpload,
  StartDateEndDateDTO,
} from "@/modules/Organization/dtos/organization.dto";

class PaymentsService {
  private restClient: RestClientService;

  constructor() {
    this.restClient = new RestClientService();
  }

  public async fetchPayments(
    params: FetchPaymentsParamsRequestModel,
  ): Promise<IPayments> {
    let mappedPayments: IPayments = {
      totalRecords: 0,
      hasNext: false,
      pageNumber: 0,
      pageSize: 0,
      totalPages: 0,
      payments: [],
    };
    try {
      const response: ICoreResponse<PaymentsResponseModel> =
        await this.restClient.get(PAYMENTS_API_PATH.GET, params);

      if (response.success && response.data) {
        const paymentsMapper = new PaymentsMapper();
        mappedPayments = paymentsMapper.mapPaymentsResponse(response.data);
      } else {
        notificationService.type = NOTIFICATION_TYPES.ERROR;
        notificationService.description =
          response.message || response.error || NOTIFICATION_MESSAGES.DEFAULT;
        notificationService.triggerNotification();
      }
    } catch (err) {
      console.error("Error in fetchPayments:", err);
    }
    return mappedPayments;
  }

  public async fetchPayment(id: string): Promise<IPayment | null> {
    let mappedPayment: IPayment | null = null;
    try {
      const response: ICoreResponse<PaymentDetailResponseModel> =
        await this.restClient.get(
          formatString(PAYMENTS_API_PATH.GET_BY_ID, id),
        );

      if (response.success && response.data) {
        const paymentsMapper = new PaymentsMapper();
        mappedPayment = paymentsMapper.mapPaymentResponse(response.data);
      } else {
        notificationService.type = NOTIFICATION_TYPES.ERROR;
        notificationService.description =
          response.message || response.error || NOTIFICATION_MESSAGES.DEFAULT;
        notificationService.triggerNotification();
      }
    } catch (err) {
      console.error("Error in fetchPayment:", err);
    }
    return mappedPayment;
  }

  public async createPayment(
    body: CreatePaymentBodyRequest,
    callback: (id: string) => void,
  ): Promise<IPayment | null> {
    let mappedPayment: IPayment | null = null;

    try {
      const response: ICoreResponse<PaymentDetailResponseModel> =
        await this.restClient.post(PAYMENTS_API_PATH.POST, body);

      if (response.success && response.data) {
        const paymentsMapper = new PaymentsMapper();
        mappedPayment = paymentsMapper.mapPaymentResponse(response.data);

        notificationService.notificationStyle = NOTIFICATION_STYLES.POPUP;
        notificationService.actionButtonText =
          mappedPayment?.transactionType === PAYMENT_METHODS_TYPES.ONE_BILL
            ? "How to pay"
            : "Proceed to pay";
        notificationService.action = () => {
          const url =
            mappedPayment?.transactionType === PAYMENT_METHODS_TYPES.ONE_BILL
              ? payGuideUrl
              : mappedPayment?.creditCardlDetail?.paymentUrl;
          window.open(url);
        };
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.title = "Payments";
        notificationService.description =
          NOTIFICATION_MESSAGES.PAYMENT_CREATE_SUCCESS;
        callback(mappedPayment.id);
      } else {
        notificationService.notificationStyle = NOTIFICATION_STYLES.SNACKBAR;
        notificationService.type = NOTIFICATION_TYPES.ERROR;
        notificationService.description =
          response.message || response.error || NOTIFICATION_MESSAGES.DEFAULT;
      }
    } catch (err) {
      console.error("Error in createPayment:", err);
    }
    notificationService.triggerNotification();

    return mappedPayment;
  }

  public getAllPaymentsByDate(
    payload: StartDateEndDateDTO,
  ): Promise<ICoreResponse> {
    return this.restClient.get(
      PAYMENTS_API_PATH.GET_PAYMENTS_BY_DATE_RANGE,
      payload,
    );
  }

  public getAllPaymentMethods(organization_id: string): Promise<ICoreResponse> {
    return this.restClient.get(
      PAYMENTS_API_PATH.GET_PAYMENT_LIST + organization_id,
    );
  }

  public paymentContentUpload(
    body: ContentUpload,
    financialProfilePublicId: string,
  ): Promise<ICoreResponse> {
    const formdata = new FormData();
    const config = {
      headers: { "Content-Type": "multipart/form-data" },
    };

    formdata.append("content", body.content, body.content.name);
    formdata.append("content_category", body.content_category);
    formdata.append("content_sub_category", body.content_sub_category);
    if (body.content_type) {
      formdata.append("content_type", body.content_type);
    }

    return this.restClient.post(
      formatString(PAYMENTS_API_PATH.CONTENT_UPLOAD, financialProfilePublicId),
      formdata,
      config,
    );
  }

  public async updateBankTransfer(
    paymentId: string,
    body: UpdateBankTransferRequestModel,
  ): Promise<ICoreResponse<PaymentDetailResponseModel>> {
    try {
      const response: ICoreResponse<PaymentDetailResponseModel> =
        await this.restClient.put(
          formatString(PAYMENTS_API_PATH.UPDATE_BANK_TRANSFER, paymentId),
          body,
        );

      if (response.success && response.data) {
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          response.message || "Bank transfer updated successfully";
        notificationService.triggerNotification();
      } else {
        notificationService.type = NOTIFICATION_TYPES.ERROR;
        notificationService.description =
          response.message || response.error || NOTIFICATION_MESSAGES.DEFAULT;
        notificationService.triggerNotification();
      }

      return response;
    } catch (err) {
      console.error("Error in updateBankTransfer:", err);
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description = NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
      throw err;
    }
  }

  public async fetchSystemBanks(
    paymentId?: string,
    isFinanceUser = false,
  ): Promise<ICoreResponse> {
    const shouldAppendPaymentId = isFinanceUser && Boolean(paymentId);
    const url = shouldAppendPaymentId
      ? `${PAYMENTS_API_PATH.GET_SYSTEM_BANKS}?payment_id=${paymentId}`
      : PAYMENTS_API_PATH.GET_SYSTEM_BANKS;

    return this.restClient.get(url);
  }
}

export default PaymentsService;
